<template>
  <div>
    <h3 class="display-4" @click="enableDebug($event)">Manage Jobs</h3>

    <o-list-toolbar
      :filters-list="filter_list"
      :list-options="list_options"
      :new-to="{ name: 'job.create', params: { id: 0 } }"
      :query-select-options="options"
      :schema="schema"
      :search-fields-list="search_field_list"
      :selected-filter="selected_filter"
      new-button-text="New Job"
      @filter_changed="update_filter($event)"
      @options_changed="update_table($event)"
      @reset_table="reset_table"
    >
    </o-list-toolbar>

    <b-table
      :fields="fields"
      :items="items"
      :sort-by="list_options.sort"
      :sort-desc="list_options.order == 'desc'"
      :tbody-tr-class="rowClass"
      bordered
      class="o-list"
      head-variant="light"
      no-local-sorting
      thead-class="o-gradient-header"
      @sort-changed="change_sort"
    >
      <template slot="cell(customer)" slot-scope="{ index, item }">
        <lookup-display
          :list="optionCustomerItemsById"
          :value="item.customer"
          default-value="-"
          display-field="name"
        ></lookup-display>
        &nbsp;
        <span v-if="item.on_hold" class="badge badge-warning">ON HOLD</span>
      </template>
      <template slot="cell(order_date)" slot-scope="{ index, item }">
        <date-display
          :value="item.order_date"
          format="DD/MM/YYYY hh:mm"
        ></date-display>
      </template>
      <template slot="cell(po_number)" slot-scope="{ index, item }">
        <default-display :value="item.po_number"></default-display>
      </template>
      <template slot="cell(delivery_address)" slot-scope="{ index, item }">
        <router-link :to="{ name: 'job.edit', params: { id: item.id } }">
          <default-display
            :value="item.delivery_address"
            default-value="-- Unknown Address --"
          ></default-display>
        </router-link>
      </template>
      <template slot="cell(on_site_contact)" slot-scope="{ index, item }">
        <lookup-display
          :list="optionOnSiteContactItemsById"
          :value="item.on_site_contact"
          default-value="-"
          display-field="name"
        ></lookup-display>
        &nbsp;
        <lookup-display
          :list="optionOnSiteContactItemsById"
          :value="item.on_site_contact"
          display-field="phone_number"
        ></lookup-display>
      </template>
      <template
        slot="cell(requested_delivery_date)"
        slot-scope="{ index, item }"
      >
        <date-display
          :value="item.requested_delivery_date"
          format="DD/MM/YYYY"
        ></date-display>
      </template>
      <template
        slot="cell(requested_scrap_pickup_date)"
        slot-scope="{ index, item }"
      >
        <date-display
          :value="item.requested_scrap_pickup_date"
          format="DD/MM/YYYY"
        ></date-display>
      </template>
      <template slot="cell(scrap_pickup)" slot-scope="{ index, item }">
        <boolean-display :value="item.scrap_pickup"></boolean-display>
      </template>

      <template slot="empty">
        <p>
          <emoticon-sad-icon large></emoticon-sad-icon>
        </p>
        No Data Found. Please
        <router-link :to="{ name: 'job.create' }"
        >create a new item
        </router-link
        >
      </template>
    </b-table>

    <o-list-paging
      :limit="list_options.limit"
      :offset="list_options.offset"
      :page-options="rows_per_page_items"
      :total-items="total_items"
      @change-limit="change_limit"
      @change-page="change_page"
    />
  </div>
</template>

<style>
.row--2 {
  background-color: #f9f9f9;
}

.row--2 {
  background-color: #efefef;
}

.row--1 {
  background-color: #e3e3e3;
}

.row-0 {
  background-color: #c1cdcd;
}

.row-1 {
  background-color: #c0ff97;
}

.row-2 {
  background-color: #edef85;
}

.row-3 {
  background-color: #fbb;
}

.row-4 {
  background-color: #fbb;
}
</style>

<script>
import {CreateCodeTableFetchMixin, CreateGroupPermissionMixin, CreateListPageMixin,} from "../../../vue-ocom/mixins";
import {fieldToTableColumn, GetSearchFields,} from "../../../vue-ocom/schema/schema_utils";
import {cleanUpModel} from "../../../vue-ocom/schema/schema_vuetify";
import schema from "../../schema/index";

import OListToolbar from "../../../vue-ocom/components/list/OListToolbar";
import OListPaging from "../../../vue-ocom/components/list/OListPaging";
import DefaultDisplay from "../../../vue-ocom/components/display/DefaultDisplay";
import DateDisplay from "../../../vue-ocom/components/display/DateDisplay";
import NumberDisplay from "../../../vue-ocom/components/display/NumberDisplay";
import BooleanDisplay from "../../../vue-ocom/components/display/BooleanDisplay";
import LookupDisplay from "../../../vue-ocom/components/display/LookupDisplay";
import EmoticonSadIcon from "vue-material-design-icons/EmoticonSad";
import EmailIcon from "vue-material-design-icons/Email";
import LinkIcon from "vue-material-design-icons/Link";

export default {
  components: {
    EmoticonSadIcon,
    EmailIcon,
    LinkIcon,
    OListPaging,
    OListToolbar,
    DateDisplay,
    DefaultDisplay,
    BooleanDisplay,
    NumberDisplay,
    LookupDisplay,
  },
  mixins: [
    CreateListPageMixin("Job", "job"),
    CreateGroupPermissionMixin("Job"),
    CreateCodeTableFetchMixin([
      "Customer",
      "OnSiteContact",
      "CodeArea",
      "CodeTimeOfDay",
      "CodeTimeOfDay",
    ]),
  ],
  data: function () {
    return {
      schemaModel: cleanUpModel(schema.Job, {}) /*Use this.schema instead */,

      search_field_list: GetSearchFields(schema.Job).concat([
        {
          text: "Customer Name",
          value: "customer__name",
        },
      ]),

      filter_list: [
        {
          name: "My Job",
          code: "my_job",
          options: {offset: 0},
        },
        {
          name: "Not Delivered",
          code: "not_delivered",
          options: {offset: 0, sort: "requested_delivery_date"},
        },
        {
          name: "To Be Picked Up",
          code: "to_be_picked_up",
          options: {offset: 0, sort: "requested_delivery_date"},
        },
        {
          name: "Not Invoiced",
          code: "not_invoiced",
          options: {offset: 0, sort: "requested_delivery_date"},
        },
        {
          name: "Invoiced",
          code: "invoiced",
          options: {offset: 0, sort: "requested_delivery_date"},
        },
        {
          name: "All",
          code: "active",
          options: {offset: 0, sort: "requested_delivery_date"},
        },
        {
          name: "Inactive",
          code: "inactive",
          options: {offset: 0, sort: "requested_delivery_date"},
        },
        {
          name: "On Hold",
          code: "onhold",
          options: {offset: 0, sort: "requested_delivery_date"},
        },
      ],
    };
  },
  methods: {
    rowClass(item, type) {
      if (!item) return;

      var moment = require("moment");

      var days = moment(item.requested_delivery_date).diff(moment(), "d");

      return ["row-" + days];
    },
  },
  computed: {
    options() {
      return {
        Customer: {
          options: this.optionCustomerItems,
          textField: "name",
          valueField: "id",
        },
        OnSiteContact: {
          options: this.optionOnSiteContactItems,
          textField: "name",
          valueField: "id",
        },
        CodeArea: {
          options: this.optionCodeAreaItems,
          textField: "code",
          valueField: "id",
        },
        CodeTimeOfDay: {
          options: this.optionCodeTimeOfDayItems,
          textField: "description",
          valueField: "id",
        },
      };
    },
    fields() {
      return this.filterTableColumns(this.schema, [
        fieldToTableColumn(this.schema, "customer"),
        fieldToTableColumn(this.schema, "order_date"),
        fieldToTableColumn(this.schema, "po_number"),
        fieldToTableColumn(this.schema, "delivery_address"),
        fieldToTableColumn(this.schema, "on_site_contact"),
        fieldToTableColumn(this.schema, "requested_delivery_date"),
        fieldToTableColumn(this.schema, "requested_scrap_pickup_date"),
        fieldToTableColumn(this.schema, "scrap_pickup"),
      ]);
    },
  },
};
</script>
